/**
 * Snippet caption derivation — the descriptive half of a capture
 * (docs/design/SNIPPETS-SPEC.md "Caption").
 *
 * v1, honest version: a Roman-numeral sequence plus a cheap cadence read at
 * the tail, in handwriting voice (foundation §7). Full "which moves does
 * this passage embody" inference is deferred to Steal from Masters —
 * deliberately not attempted here.
 */
import type { ChordQuality, MusicalKey, ScaleDegree } from '@/types/chord';
import type { SnippetChord } from '@/types/snippet';

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];

/**
 * scaleDegree + quality -> Roman numeral, reading case and °/+ off the
 * chord's actual quality (mirrors the convention in
 * GhostChordSuggestion.romanFor — kept local since that helper isn't
 * exported and the two call sites don't otherwise share code).
 */
export function romanNumeralFor(scaleDegree: ScaleDegree, quality: ChordQuality): string {
  const base = ROMAN[scaleDegree] ?? '';
  const isMinorish =
    quality === 'minor' ||
    quality.startsWith('min') ||
    quality === 'diminished' ||
    quality.startsWith('dim') ||
    quality === 'halfdim7';
  let roman = isMinorish ? base.toLowerCase() : base;
  if (quality === 'diminished' || quality.startsWith('dim') || quality === 'halfdim7') roman += '°';
  if (quality === 'augmented') roman += '+';
  return roman;
}

const FLAT_KEY_NAMES: Partial<Record<MusicalKey, string>> = {
  Db: 'D flat',
  Eb: 'E flat',
  Gb: 'G flat',
  Ab: 'A flat',
  Bb: 'B flat',
};

/** "Eb" -> "E flat"; naturals pass through unchanged. */
export function keyDisplayName(key: MusicalKey): string {
  return FLAT_KEY_NAMES[key] ?? key;
}

type CadenceKind = 'authentic' | 'half' | 'plagal' | 'deceptive';

/**
 * Cheap cadence read at the tail of a passage, from scaleDegree alone —
 * structural, not the pitch-based src/lib/analysis/cadence-detector.ts
 * (which needs raw NoteGroup/tempo data from audio import that a captured,
 * already-labeled Chord[] doesn't have). Same four categories, simpler input.
 */
function detectCadenceKind(chords: SnippetChord[]): CadenceKind | null {
  if (chords.length < 2) return null;
  const last = chords[chords.length - 1];
  const prev = chords[chords.length - 2];

  if (prev.scaleDegree === 5 && last.scaleDegree === 1) return 'authentic';
  if (prev.scaleDegree === 5 && last.scaleDegree === 6) return 'deceptive';
  if (prev.scaleDegree === 4 && last.scaleDegree === 1) return 'plagal';
  if (last.scaleDegree === 5) return 'half';
  return null;
}

/**
 * Does the cadential chord (the one resolving INTO the final chord — e.g.
 * the V of a 4-3 suspension into I) carry a suspension? Cheap extensions-flag
 * read, not full suspension-connector detection.
 */
function endsSuspended(chords: SnippetChord[]): boolean {
  const prev = chords[chords.length - 2];
  return !!(prev?.extensions?.sus2 || prev?.extensions?.sus4);
}

function cadenceDescription(kind: CadenceKind, suspended: boolean): string {
  switch (kind) {
    case 'authentic':
      return suspended ? 'the suspended arrival' : 'the arrival';
    case 'deceptive':
      return 'the deceptive arrival';
    case 'plagal':
      return 'the plagal close';
    case 'half':
      return 'left open on the dominant';
  }
}

/**
 * Format: "IV – V – vi in E flat — the deceptive arrival", falling back to
 * just the numerals when no cadence pattern is recognized at the tail.
 */
export function deriveCaption(chords: SnippetChord[], key: MusicalKey): string {
  const numerals = chords.map((c) => romanNumeralFor(c.scaleDegree, c.quality)).join(' – ');
  const cadence = detectCadenceKind(chords);
  if (!cadence) return numerals;

  const description = cadenceDescription(cadence, endsSuspended(chords));
  return `${numerals} in ${keyDisplayName(key)} — ${description}`;
}
